import React from 'react';
import { withData } from '../DataController';
import { getStyle as convertTemplateStyle } from '../Utils/styleConversion';
import { debounce } from 'throttle-debounce';
import { getTableStyles } from '../components/Shared/EditableUtils';
import { sidebysides, simpleInlineStyles } from '@threeskye/editor-component-slate';
import { Transforms, Node as SlateNode, Path as SlatePath } from 'slate';
import { ReactEditor } from 'slate-react';
import { MenuItem } from 'react-contextmenu';
import { addListItemOnEnter } from '@threeskye/editor-component-slate/dist/menu/Utils.js'
import { toastSuccess } from '../components/popups/Toast'
import Editor from './Editor';
import TableRowStyleMenuItem from '@threeskye/editor-component-slate/dist/engine/context/table/TableRowStyleMenuItem';
import TableColumnStyleMenuItem from '@threeskye/editor-component-slate/dist/engine/context/table/TableColumnStyleMenuItem';
import EditorSubMenu from '@threeskye/editor-component-slate/dist/engine/context/EditorSubMenu';
import "./SlateStaticData.scss"

class SlateStaticData extends React.Component {

	constructor(props) {
		super(props);

		this.state = {
			content: null,
			editorKey: 1,
			editorId: '_' + Math.random().toString(36).substr(2, 9),
			addins: [],
			editable: false,
			toolbar: { type: "full" }
		}


		this.onChange = debounce(1500, this.onChange.bind(this));
		this.listener = this.listener.bind(this);

		this.tableStyles = getTableStyles(props.styles, props.config.rowStyleOptions, props.config.columnStyleOptions, props.data);

		this.tableStyleRef = React.createRef();

		this.rowStyles = props.config.rowStyleOptions && props.config.rowStyleOptions.map(option => <MenuItem onClick={() => this.tableStyleRef.current.setRowStyle(option.key)}>{option.label}</MenuItem>);

		this.setToolbar = this.setToolbar.bind(this);

		this.throwToaster = this.throwToaster.bind(this);

	}


	componentDidMount() {
		if (this.props.RawEditableText) {
			this.listener(null, null, this.props.load);
			return
		};
		this.props.data.addListener(this.listener);
		this.props.data.addLocalListener(this.listener);
	}

	componentWillUnmount() {
		if (this.props.RawEditableText) return;
		this.props.data.removeListener(this.listener);
		this.props.data.removeLocalListener(this.listener);
	}

	setToolbar(toolbar) {
		this.setState({ toolbar: toolbar })
	}

	throwToaster() {
		toastSuccess('Link Copied')
	}

	listener(src, srcDataName, genericContent) {
		if (srcDataName && this.props.config.dataName !== srcDataName) {
			return new Promise((resolve) => resolve(false));

		}
		if (src && src === this.state.editorId) {
			return new Promise((resolve) => resolve(false));
		}
		this.setState({ editable: false })
		const config = this.props.config;
		// Raw hosts (previews, the compare view) mount with data={{getStyle}} only —
		// content arrives via the load prop (passed through as genericContent), and
		// calling data.getValue here would throw.
		const content = this.props.RawEditableText
			? genericContent
			: this.props.data.getValue(this.props.config.value)

		if (content && content !== null && content !== "") {
			//FIXME editor not handling updating props, so change whole component. BAD! fix in editor.
			this.setState({ content: content, editorKey: this.state.editorKey + 1 });
		} else {
			this.setState({ content: null, editorKey: this.state.editorKey + 1 });
		}

		const { styles } = this.props;

		const myStyles = Object.keys(config.styles).map(key => {
			const actual = styles[config.styles[key]];
			if (!actual) return null
			const converted = this.props.data && this.props.data.getStyle ? this.props.data.getStyle(actual) : {};
			converted.lineHeight = "1.2";
			const val = {};
			val[key] = converted;
			return val;
		});
		
		const tableStyles = config.tableStyles;
		const defaultCellStyle = this.props.data && this.props.data.getStyle ? this.props.data.getStyle(styles[config.styles.cell], true) : {};
		const cellHeaderStyle = this.props.data && this.props.data.getStyle ? this.props.data.getStyle(styles[config.styles.cellHeader], true) : {};

		//browser default cellHeader is text-align: center... kill this
		if (cellHeaderStyle && !cellHeaderStyle.hasOwnProperty("textAlign")) {
			cellHeaderStyle.textAlign = "left";
		}

		const flattenedStyles = {};
		myStyles.forEach(style => Object.assign(flattenedStyles, style));
		const tdStyle = {};
		const thStyle = {};

		const paddingBelowTable = config.paddingBelowTable;
		if (paddingBelowTable) {
			flattenedStyles.table = {
				marginBottom: paddingBelowTable + "pt"
			}
		} else {
			flattenedStyles.table = {
				marginBottom: tableStyles.cellVerticalPadding + "pt"
			}
		}

		Object.assign(tdStyle, defaultCellStyle);
		flattenedStyles.td = tdStyle;
		Object.assign(thStyle, cellHeaderStyle);
		flattenedStyles.th = thStyle;

		if (tableStyles.tableBorder) {
			const borderStyle = this.props.data && this.props.data.getStyle ? this.props.data.getStyle(styles[tableStyles.tableBorder]) : {};
			flattenedStyles['table-table'] = borderStyle;
		}

		if (flattenedStyles["tableBorder"]) {
			flattenedStyles["table-border"] = {
				"border": `${flattenedStyles["tableBorder"].borderLeftWidth} ${flattenedStyles["tableBorder"].borderLeftStyle} ${flattenedStyles["tableBorder"].borderLeftColor}`
			};
		}

		const menuItems = [];
		if (config.rowStyleOptions) {
			menuItems.push(
				<EditorSubMenu title='Row styles'>
					{config.rowStyleOptions.map((option, idx) => <TableRowStyleMenuItem key={"rowStyleOptions" + idx} styleName={option.key} text={option.label} showPreview={true} />)}
				</EditorSubMenu>
			)
		}
		if (config.columnStyleOptions) {
			menuItems.push(
				<EditorSubMenu title='Column styles'>
					{config.columnStyleOptions.map((option, idx) => <TableColumnStyleMenuItem key={"columnStyleOptions" + idx} styleName={option.key} text={option.label} showPreview={true} />)}
				</EditorSubMenu>
			)
		}

		const tableOptions = {
			initialFooter: true,
			menuItems: menuItems,
			styleRef: this.tableStyleRef,
			styleInterpreter: style => {
				if (style === 'bordered-table') {
					return flattenedStyles["table-border"]
				}

				for (const { label, key } of config.rowStyleOptions) {
					if (style === key) {
						return this.tableStyles[key];
					}
				}
				for (const { label, key } of config.columnStyleOptions) {
					if (style === key) {
						return this.tableStyles[key];
					}
				}
				return null;
			},
			hoverOptions: ["border"] // TODO: Hover options should be configurable (from template/config)
		};

		const imageOptions = {
			menuItems: ["border"],
			styleInterpreter: style => {
				if (style === 'with-border') {
					return myStyles["image-border"]
				}
				return null;
			},
			contextMenu: {
				// this will break visually if not both true, 
				// TODO - update 3skye-slate to handle both horiz and vert, iconMenuItems and checkedMenuItems
				horizontal: true,
				iconButton: true
			},
			contextMenuOrder: [
				0,
				1,
				2,
				3,
				"divider",
				"border",
				"divider",
				"header",
				"footer",
				"divider",
				"createSBS",
				"breakSBS",
				"divider",
				"delete"
			]
		};

		const sbsOptions = {
			styleInterpreter: style => {
				if (style && style.includes("with-gap") && style.includes("with-border")) {
					return { ...myStyles["with-border"], gap: myStyles["sbsGap"] ? myStyles["sbsGap"].fontSize : 12 }

				} else if (style === "with-border") {
					return myStyles["with-border"]

				} else if (style === "with-gap") {
					return { gap: myStyles["sbsGap"] ? myStyles["sbsGap"].fontSize : 12 }
				}
			}
		}

		const sbs = sidebysides(tableOptions, imageOptions, sbsOptions);


		const headings = {
			install: editor => {
				return {
					getRenderer: (type, editor) => {
						if (type === "heading-one")
							return ({ attributes, children }) => <h1 {...attributes}>{children}</h1>
						else if (type === "heading-two")
							return ({ attributes, children }) => <h2 {...attributes}>{children}</h2>
						else if (type === "heading-three")
							return ({ attributes, children }) => <h3 {...attributes}>{children}</h3>
						else
							return null;
					},
					editor: editor
				}
			}
		}

		const lists = {
			install: editor => {
				return {
					getRenderer: (type, editor) => {
						if (type === "list-item")
							return ({ attributes, children, element }) => {
								const path = ReactEditor.findPath(editor, element)
								const parentPath = SlatePath.parent(path);
								const grandpaPath = SlatePath.parent(parentPath);
								const grandpaNode = SlateNode.get(editor, grandpaPath)
								const isCellChild = grandpaNode && grandpaNode.type === 'table-cell'
								const style = isCellChild ? { margin: 0, color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit', lineHeight: 'inherit' } : null

								const updatechildren = children.map((child) => {
									if (child.props.text && child.props.text.url !== undefined && child.props.text.url !== null) {
										const url = child.props.text.url
										const href = (url.includes('http://') || url.includes('https://')) ? url : 'http://' + url
										const colour = child.props.text.color
										const decoration = child.props.text.underline ? 'underline' : 'none'
										const text = child.props.text.text
										const linkStyles = { fontWeight: 600, cursor: 'pointer', color: colour || '#A9754E', textDecoration: decoration }
										return (
											<a target='_blank' href={href} style={linkStyles} onClick={(e) => this.setToolbar({ type: 'link-tooltip', text: text, href: href })} {...attributes}>
												{child}
											</a>
										)
									} else return child
								})
								return <li {...attributes} style={style} >{updatechildren}</li>
							}
						else if (type === "numbered-list")
							return ({ attributes, children, element }) => {
								const path = ReactEditor.findPath(editor, element)
								const parentPath = SlatePath.parent(path);
								const parentNode = SlateNode.get(editor, parentPath)
								const isCellChild = parentNode && parentNode.type === 'table-cell'
								const style = isCellChild ? { margin: 0, color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit', lineHeight: 'inherit' } : null
								return <ol {...attributes} style={style}>{children}</ol>
							}
						else if (type === "bulleted-list")
							return ({ attributes, children, element }) => {
								const path = ReactEditor.findPath(editor, element)
								const parentPath = SlatePath.parent(path);
								const parentNode = SlateNode.get(editor, parentPath)
								const isCellChild = parentNode && parentNode.type === 'table-cell'
								const style = isCellChild ? { margin: 0, color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit', lineHeight: 'inherit' } : null
								return <ul {...attributes} style={style}>{children}</ul>
							}
						else
							return null;
					},
					onKeyDown: (event) => {
						if (event.key === 'Enter') {
							const { selection } = editor;
							if (!selection || !editor.selection) return;

							const path = selection.anchor.path
							const rootNode = editor.children[path[0]]

							if (rootNode.type === 'bulleted-list' || rootNode.type === 'numbered-list') {
								const isLink = path.length === 3 && rootNode.children[path[1]].children[path[2]].url !== undefined
								if (isLink) {
									const anchor = selection.anchor.offset
									const focus = selection.focus.offset
									const spaceAtTheStartOrTheEnd = anchor === focus && (anchor === 0 || anchor === rootNode.children[path[1]].children[path[2]].text.length)

									if (spaceAtTheStartOrTheEnd) {
										event.preventDefault()
										addListItemOnEnter(editor, path, anchor === 0)
									}
								} else {
									const listItemIndex = path[1];
									const listItem = rootNode.children[listItemIndex];
									const isEmptyListItem = listItem.children.length === 1 && listItem.children[0].text.trim() === '';

									if (isEmptyListItem) {
										event.preventDefault();

										// Split the list into two parts
										const listBefore = rootNode.children.slice(0, listItemIndex);
										const listAfter = rootNode.children.slice(listItemIndex + 1);

										// Remove current list node
										Transforms.removeNodes(editor, { at: [path[0]] });

										// Insert first part of list if it exists
										if (listBefore.length > 0) {
											Transforms.insertNodes(editor, { type: rootNode.type, children: listBefore }, { at: [path[0]] });
										}

										// Insert empty paragraph between lists
										const paragraphIndex = listBefore.length > 0 ? path[0] + 1 : path[0];
										Transforms.insertNodes(editor, { type: 'text', children: [{ text: '' }] }, { at: [paragraphIndex] });

										// Insert second part of list if it exists
										if (listAfter.length > 0) {
											Transforms.insertNodes(editor, { type: rootNode.type, children: listAfter }, { at: [paragraphIndex + 1] });
										}

										// Move selection to new paragraph
										Transforms.select(editor, {
											anchor: { path: [paragraphIndex, 0], offset: 0 },
											focus: { path: [paragraphIndex, 0], offset: 0 },
										});

										return;
									}
								}
							}
						}
					},
					editor: editor

				}
			}
		}

		// additionalAddins: extra editor addins injected by read-only hosts (e.g. the
		// compare view's diff-marks addin) — same {install} shape as the built-ins.
		this.setState({ addins: simpleInlineStyles().concat([sbs, headings, lists]).concat(this.props.additionalAddins || []), flattenedStyles, editable: true });
		return new Promise((resolve) => resolve(true));

	}

	//onChange(data, overflown, initial) {
	onChange(data, initial) {
		if (initial)
			return;
		if (this.props.RawEditableText) {
			// Raw mode has no data controller — withRawComponent substitutes
			// data={{ getStyle }}, so updateLocalData never exists here. Hosts that
			// persist pass onChange; read-only hosts (compare panes) pass none,
			// and a normalisation onChange must be a no-op.
			if (this.props.onChange)
				this.props.onChange(data);
			return;
		}
		const dataName = this.props.config.dataName + (this.props.dataSuffix ? this.props.dataSuffix : '');
		this.props.data.updateLocalData(dataName, JSON.stringify(data), this.state.editorId, this.props.config.dataName);
	}


	render() {
		const config = this.props.config;
		const styles = this.props.styles;
		const foundStyle = styles["brand-colour-one"] || styles["company-header"] || styles["page-header"] || styles["logo"] || null
		const colour1 = foundStyle && foundStyle.color ? foundStyle.color : null
		const colourOptions = colour1 ? [colour1, '#2536f0'] : ['#2536f0']

		const options = {
			disableSideBySide: config.disableSideBySide
		};

		if (config.rowStyleOptions) {
			options.rowStyleOptions = config.rowStyleOptions;

		}
		if (config.columnStyleOptions) {
			options.columnStyleOptions = config.columnStyleOptions;
		}

		let flattenedStyles = this.state.flattenedStyles;
		//TODO style options
		//Object.assign(flattenedStyles, this.tableStyles);


		return (
			<div className='slate-static-data'>
				<Editor
					key={this.state.editorKey}
					readOnly={true}
					options={options}
					load={this.state.content}
					width={config.width}
					height={config.height}
					styles={styles}
					myStyles={flattenedStyles || {}}
					onChange={this.onChange}
					addins={this.state.addins}
					toolbar={this.state.toolbar}
					setToolbar={this.setToolbar}
					colourOptions={colourOptions}
					throwToaster={this.throwToaster}
					noEditStates={this.props.noEditStates}
					editableStyle={this.props.editableStyle}
				/>
			</div>
		)
	}
}

export default withData(SlateStaticData);

const withRawComponent = (Component) => {
	return (props) => {
		// Shared conversion (Utils/styleConversion.js) — keeps the Raw/compare path
		// identical to the editor's DataController.getStyle (option 1B).
		return <Component RawEditableText {...props} data={{ getStyle: convertTemplateStyle }} />
	}
}
export const RawEditableText = withRawComponent(SlateStaticData);